import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Add,
  ContentCopy,
  Delete,
  Edit,
  History,
  Replay,
  Save,
  TaskAlt,
  ToggleOff,
  ToggleOn,
} from '@mui/icons-material'
import moment from 'moment'
import API from '../../../../services/API'
import {
  CALL_TRIAGE_PRIORITIES,
  calculateSuggestedPriority,
  callTriageAcknowledgementText,
} from '../../../../helpers/callTriage'

const blankQuestion = () => ({
  _id: `q-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  order: 1,
  text: '',
  helpText: '',
  type: 'yesNo',
  required: false,
  options: [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ],
  rules: [],
})

const blankRule = () => ({
  order: 1,
  when: { operator: 'equals', value: 'Yes' },
  action: { type: 'minimumPriority', priority: 'High', levels: 1, message: '' },
})

const blankComplaint = {
  code: '',
  category: '',
  presentingComplaint: '',
  description: '',
  keywords: [],
  defaultPriority: 'Moderate',
  enabled: true,
  questions: [],
}

const normaliseSearch = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const splitKeywords = (value) =>
  String(value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

const statusColor = (status) => {
  if (status === 'Approved') return 'success'
  if (status === 'Awaiting Approval') return 'warning'
  if (status === 'Disabled') return 'default'
  return 'info'
}

const ApprovalDialog = ({ open, onClose, onConfirm, count = 1 }) => {
  const [confirmed, setConfirmed] = useState(false)

  useEffect(() => {
    if (open) setConfirmed(false)
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Approve Organisation-approved Triage Configuration</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning" variant="outlined">
            <Typography whiteSpace="pre-line">{callTriageAcknowledgementText}</Typography>
          </Alert>
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
            }
            label="I confirm that I have reviewed this call triage configuration and that my organisation accepts responsibility for its use."
          />
          <Typography variant="body2" color="text.secondary">
            {count} complaint{count === 1 ? '' : 's'} will become available to call
            handlers if enabled.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!confirmed}
          onClick={() => onConfirm({ confirmed, acknowledgementText: callTriageAcknowledgementText })}
        >
          Approve
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const AuditDialog = ({ complaint, onClose }) => {
  const [audit, setAudit] = useState([])

  useEffect(() => {
    if (!complaint?._id) return
    API.get(`/configuration/command-control/call-triage/${complaint._id}/audit`).then(
      (res) => {
        if (res.status === 200) setAudit(res.data)
      },
    )
  }, [complaint?._id])

  return (
    <Dialog open={!!complaint} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Call Triage Audit</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          {audit.map((entry) => (
            <Paper key={entry._id} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
              <Typography fontWeight={800}>{entry.action}</Typography>
              <Typography variant="body2" color="text.secondary">
                {moment(entry.changedAt).format('DD MMM YYYY HH:mm')} by{' '}
                {[entry.changedBy?.firstName, entry.changedBy?.lastName]
                  .filter(Boolean)
                  .join(' ') || 'Unknown user'}
              </Typography>
              {entry.reason && <Typography variant="body2">{entry.reason}</Typography>}
            </Paper>
          ))}
          {!audit.length && <Alert severity="info">No audit entries recorded.</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

const QuestionEditor = ({ question, onChange, onRemove }) => {
  const updateRule = (index, patch) => {
    const rules = [...(question.rules ?? [])]
    rules[index] = { ...rules[index], ...patch }
    onChange({ ...question, rules })
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontWeight={800}>Question {question.order}</Typography>
          <Tooltip title="Remove question">
            <IconButton onClick={onRemove} size="small">
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Question text"
              value={question.text}
              onChange={(e) => onChange({ ...question, text: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={question.type}
                onChange={(e) => onChange({ ...question, type: e.target.value })}
              >
                {['yesNo', 'singleSelect', 'multiSelect', 'number', 'text'].map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Help text"
              value={question.helpText}
              onChange={(e) => onChange({ ...question, helpText: e.target.value })}
              fullWidth
            />
          </Grid>
        </Grid>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!question.required}
              onChange={(e) => onChange({ ...question, required: e.target.checked })}
            />
          }
          label="Required"
        />

        {['yesNo', 'singleSelect', 'multiSelect'].includes(question.type) && (
          <Stack spacing={1}>
            <Typography fontWeight={800}>Options</Typography>
            {(question.options ?? []).map((option, index) => (
              <Grid container spacing={1} key={index}>
                <Grid item xs={6}>
                  <TextField
                    label="Label"
                    value={option.label}
                    onChange={(e) => {
                      const options = [...question.options]
                      options[index] = { ...option, label: e.target.value }
                      onChange({ ...question, options })
                    }}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Value"
                    value={option.value}
                    onChange={(e) => {
                      const options = [...question.options]
                      options[index] = { ...option, value: e.target.value }
                      onChange({ ...question, options })
                    }}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            ))}
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() =>
                onChange({
                  ...question,
                  options: [...(question.options ?? []), { label: '', value: '' }],
                })
              }
            >
              Add option
            </Button>
          </Stack>
        )}

        <Divider />
        <Stack spacing={1}>
          <Typography fontWeight={800}>Rules</Typography>
          {(question.rules ?? []).map((rule, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>WHEN</InputLabel>
                    <Select
                      label="WHEN"
                      value={rule.when?.operator ?? 'equals'}
                      onChange={(e) =>
                        updateRule(index, {
                          when: { ...(rule.when ?? {}), operator: e.target.value },
                        })
                      }
                    >
                      {[
                        'equals',
                        'notEquals',
                        'contains',
                        'notContains',
                        'greaterThan',
                        'greaterThanOrEqual',
                        'lessThan',
                        'lessThanOrEqual',
                        'exists',
                        'notExists',
                      ].map((operator) => (
                        <MenuItem key={operator} value={operator}>
                          {operator}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    label="Value"
                    value={rule.when?.value ?? ''}
                    size="small"
                    fullWidth
                    onChange={(e) =>
                      updateRule(index, {
                        when: { ...(rule.when ?? {}), value: e.target.value },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>THEN</InputLabel>
                    <Select
                      label="THEN"
                      value={rule.action?.type ?? 'minimumPriority'}
                      onChange={(e) =>
                        updateRule(index, {
                          action: { ...(rule.action ?? {}), type: e.target.value },
                        })
                      }
                    >
                      {[
                        'setPriority',
                        'minimumPriority',
                        'increasePriority',
                        'decreasePriority',
                        'showWarning',
                        'requireOverrideReason',
                        'showQuestion',
                        'hideQuestion',
                      ].map((action) => (
                        <MenuItem key={action} value={action}>
                          {action}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Priority</InputLabel>
                    <Select
                      label="Priority"
                      value={rule.action?.priority ?? 'High'}
                      onChange={(e) =>
                        updateRule(index, {
                          action: { ...(rule.action ?? {}), priority: e.target.value },
                        })
                      }
                    >
                      {CALL_TRIAGE_PRIORITIES.map((priority) => (
                        <MenuItem key={priority} value={priority}>
                          {priority}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={1}>
                  <TextField
                    label="Levels"
                    value={rule.action?.levels ?? 1}
                    size="small"
                    type="number"
                    fullWidth
                    onChange={(e) =>
                      updateRule(index, {
                        action: { ...(rule.action ?? {}), levels: Number(e.target.value) },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    label="Message"
                    value={rule.action?.message ?? ''}
                    size="small"
                    fullWidth
                    onChange={(e) =>
                      updateRule(index, {
                        action: { ...(rule.action ?? {}), message: e.target.value },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={1}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      onChange({
                        ...question,
                        rules: question.rules.filter((_, ruleIndex) => ruleIndex !== index),
                      })
                    }
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() =>
              onChange({
                ...question,
                rules: [
                  ...(question.rules ?? []),
                  { ...blankRule(), order: (question.rules?.length ?? 0) + 1 },
                ],
              })
            }
          >
            Add rule
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}

const ComplaintEditor = ({ complaint, open, onClose, onSaved }) => {
  const [form, setForm] = useState(blankComplaint)
  const [answers, setAnswers] = useState({})
  const preview = useMemo(
    () =>
      calculateSuggestedPriority({
        defaultPriority: form.defaultPriority,
        questions: form.questions,
        answers,
      }),
    [form.defaultPriority, form.questions, answers],
  )

  useEffect(() => {
    setForm(complaint ? JSON.parse(JSON.stringify(complaint)) : blankComplaint)
    setAnswers({})
  }, [complaint, open])

  const save = async () => {
    const payload = {
      ...form,
      keywords: Array.isArray(form.keywords)
        ? form.keywords.map((item) => String(item).trim()).filter(Boolean)
        : splitKeywords(form.keywords),
      questions: (form.questions ?? []).map((question, index) => ({
        ...question,
        order: index + 1,
        rules: (question.rules ?? []).map((rule, ruleIndex) => ({
          ...rule,
          order: ruleIndex + 1,
        })),
      })),
    }
    const res = form._id
      ? await API.put(`/configuration/command-control/call-triage/${form._id}`, payload)
      : await API.post('/configuration/command-control/call-triage', payload)
    if ([200, 201].includes(res.status)) {
      onSaved()
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{form._id ? 'Edit Call Triage Complaint' : 'New Call Triage Complaint'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Alert severity="info" variant="outlined">
            Organisation-approved triage configuration should be reviewed before it is
            made available in live CAD.
          </Alert>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Typography fontWeight={900} gutterBottom>
              Basic details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Code"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Presenting complaint"
                  value={form.presentingComplaint}
                  onChange={(e) =>
                    setForm({ ...form, presentingComplaint: e.target.value })
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Default Suggested Priority</InputLabel>
                  <Select
                    label="Default Suggested Priority"
                    value={form.defaultPriority}
                    onChange={(e) =>
                      setForm({ ...form, defaultPriority: e.target.value })
                    }
                  >
                    {CALL_TRIAGE_PRIORITIES.map((priority) => (
                      <MenuItem key={priority} value={priority}>
                        {priority}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Search keywords"
                  helperText="Comma-separated terms call handlers may type, such as injury, limb injury, leg injury."
                  value={(form.keywords ?? []).join(', ')}
                  onChange={(e) =>
                    setForm({ ...form, keywords: splitKeywords(e.target.value) })
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!form.enabled}
                      onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                    />
                  }
                  label="Enabled"
                />
              </Grid>
            </Grid>
          </Paper>

          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography fontWeight={900}>Triage questions</Typography>
              <Button
                startIcon={<Add />}
                onClick={() =>
                  setForm({
                    ...form,
                    questions: [
                      ...(form.questions ?? []),
                      { ...blankQuestion(), order: (form.questions?.length ?? 0) + 1 },
                    ],
                  })
                }
              >
                Add question
              </Button>
            </Stack>
            {(form.questions ?? []).map((question, index) => (
              <QuestionEditor
                key={question._id}
                question={{ ...question, order: index + 1 }}
                onChange={(next) => {
                  const questions = [...form.questions]
                  questions[index] = next
                  setForm({ ...form, questions })
                }}
                onRemove={() =>
                  setForm({
                    ...form,
                    questions: form.questions.filter((_, qIndex) => qIndex !== index),
                  })
                }
              />
            ))}
          </Stack>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Typography fontWeight={900} gutterBottom>
              Preview / test
            </Typography>
            <Grid container spacing={2}>
              {(form.questions ?? []).map((question) => (
                <Grid item xs={12} md={6} key={question._id}>
                  <TextField
                    label={question.text || 'Question'}
                    helperText={question.helpText}
                    value={answers[String(question._id)] ?? ''}
                    onChange={(e) =>
                      setAnswers({
                        ...answers,
                        [String(question._id)]:
                          question.type === 'number' ? Number(e.target.value) : e.target.value,
                      })
                    }
                    select={['yesNo', 'singleSelect'].includes(question.type)}
                    type={question.type === 'number' ? 'number' : 'text'}
                    fullWidth
                  >
                    {(question.options ?? []).map((option) => (
                      <MenuItem key={option.label} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ))}
            </Grid>
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
              <Chip label={`Default Suggested Priority: ${preview.defaultPriority}`} />
              <Chip
                color="primary"
                label={`Calculated Suggested Priority: ${preview.calculatedPriority}`}
              />
              {preview.overrideReasonRequired && (
                <Chip color="warning" label="Override reason required" />
              )}
            </Stack>
            {!!preview.warnings.length && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {preview.warnings.join(' ')}
              </Alert>
            )}
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" startIcon={<Save />} onClick={save}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const CallTriage = () => {
  const [loading, setLoading] = useState(true)
  const [complaints, setComplaints] = useState([])
  const [template, setTemplate] = useState(null)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [status, setStatus] = useState('')
  const [priority, setPriority] = useState('')
  const [editor, setEditor] = useState(null)
  const [auditComplaint, setAuditComplaint] = useState(null)
  const [approvalTarget, setApprovalTarget] = useState(null)

  const load = () => {
    setLoading(true)
    API.get('/configuration/command-control/call-triage')
      .then((res) => {
        if (res.status === 200) {
          setComplaints(res.data.complaints ?? [])
          setTemplate(res.data.template)
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => load(), [])

  const categories = useMemo(
    () => [...new Set(complaints.map((item) => item.category).filter(Boolean))],
    [complaints],
  )

  const filtered = useMemo(() => {
    const terms = normaliseSearch(search).split(' ').filter(Boolean)
    return complaints.filter((item) => {
      const searchable = normaliseSearch(
        [
          item.code,
          item.category,
          item.presentingComplaint,
          item.description,
          ...(item.keywords ?? []),
        ]
          .filter(Boolean)
          .join(' '),
      )
      const matchesSearch =
        !terms.length || terms.every((term) => searchable.includes(term))
      return (
        matchesSearch &&
        (!category || item.category === category) &&
        (!status || item.status === status) &&
        (!priority || item.defaultPriority === priority)
      )
    })
  }, [complaints, search, category, status, priority])

  const hasUnapproved = complaints.some((item) =>
    ['Draft', 'Awaiting Approval'].includes(item.status),
  )

  const postAction = async (path, body = {}) => {
    const res = await API.post(path, body)
    if (res.status === 200) load()
  }

  const approve = async (payload) => {
    if (Array.isArray(approvalTarget)) {
      await postAction('/configuration/command-control/call-triage/bulk-approve', {
        ids: approvalTarget,
        ...payload,
      })
    } else {
      await postAction(
        `/configuration/command-control/call-triage/${approvalTarget._id}/approve`,
        payload,
      )
    }
    setApprovalTarget(null)
  }

  return (
    <Stack spacing={2}>
      <Typography variant="title2">Call Triage</Typography>
      {loading && <LinearProgress />}
      <Alert severity="warning" variant="outlined">
        <AlertTitle>Call Triage</AlertTitle>
        Call Triage templates help your organisation categorise calls and calculate a
        suggested priority based on your approved configuration. AmbuNet provides
        reference templates only. Your organisation is responsible for reviewing,
        approving, and maintaining these settings before use.
      </Alert>
      {hasUnapproved && (
        <Alert severity="info">
          Some call triage complaints have not been approved. Only approved and enabled
          complaints will be available to call handlers.
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Typography fontWeight={800}>Template status</Typography>
            <Typography variant="body2" color="text.secondary">
              {template?.templateName} {template?.templateVersion}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {template?.status}
            </Typography>
          </Grid>
          <Grid item xs={12} md={9}>
            <Stack direction="row" gap={1} flexWrap="wrap" justifyContent="flex-end">
              {!complaints.length && (
                <Button
                  variant="contained"
                  startIcon={<Replay />}
                  onClick={() =>
                    postAction('/configuration/command-control/call-triage/seed')
                  }
                >
                  Seed/import defaults
                </Button>
              )}
              <Button
                startIcon={<TaskAlt />}
                onClick={() =>
                  setApprovalTarget(
                    filtered
                      .filter((item) => item.status !== 'Approved' && item.status !== 'Disabled')
                      .map((item) => item._id),
                  )
                }
                disabled={!filtered.some((item) => item.status !== 'Approved' && item.status !== 'Disabled')}
              >
                Bulk approve
              </Button>
              <Button variant="outlined" startIcon={<Add />} onClick={() => setEditor({})}>
                New complaint
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={1}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.5}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {categories.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2.5}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {['Draft', 'Awaiting Approval', 'Approved', 'Disabled'].map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {CALL_TRIAGE_PRIORITIES.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ borderRadius: 1, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Presenting Complaint</TableCell>
              <TableCell>Default Suggested Priority</TableCell>
              <TableCell>Questions count</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Last updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((item) => (
              <TableRow key={item._id} hover>
                <TableCell>{item.code}</TableCell>
                <TableCell>{item.category}</TableCell>
                <TableCell>{item.presentingComplaint}</TableCell>
                <TableCell>{item.defaultPriority}</TableCell>
                <TableCell>{item.questions?.length ?? 0}</TableCell>
                <TableCell>
                  <Chip size="small" color={statusColor(item.status)} label={item.status} />
                </TableCell>
                <TableCell>{item.version}</TableCell>
                <TableCell>{item.enabled ? 'Yes' : 'No'}</TableCell>
                <TableCell>{moment(item.updatedAt).format('DD MMM YYYY HH:mm')}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" gap={0.5} justifyContent="flex-end">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditor(item)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                      <IconButton
                        size="small"
                        onClick={() =>
                          postAction(
                            `/configuration/command-control/call-triage/${item._id}/duplicate`,
                          )
                        }
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={item.enabled ? 'Disable' : 'Enable'}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          postAction(
                            `/configuration/command-control/call-triage/${item._id}/${item.enabled ? 'disable' : 'enable'}`,
                          )
                        }
                      >
                        {item.enabled ? <ToggleOff fontSize="small" /> : <ToggleOn fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset to Template">
                      <IconButton
                        size="small"
                        onClick={() =>
                          postAction(
                            `/configuration/command-control/call-triage/${item._id}/reset-to-template`,
                            { confirmed: true },
                          )
                        }
                      >
                        <Replay fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Audit">
                      <IconButton size="small" onClick={() => setAuditComplaint(item)}>
                        <History fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Approve">
                      <span>
                        <IconButton
                          size="small"
                          disabled={item.status === 'Approved' || item.status === 'Disabled'}
                          onClick={() => setApprovalTarget(item)}
                        >
                          <TaskAlt fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && (
              <TableRow>
                <TableCell colSpan={10}>
                  <Box p={3}>
                    <Alert severity="info">No call triage complaints found.</Alert>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <ComplaintEditor
        open={!!editor}
        complaint={editor?._id ? editor : null}
        onClose={() => setEditor(null)}
        onSaved={load}
      />
      <AuditDialog complaint={auditComplaint} onClose={() => setAuditComplaint(null)} />
      <ApprovalDialog
        open={!!approvalTarget}
        count={Array.isArray(approvalTarget) ? approvalTarget.length : 1}
        onClose={() => setApprovalTarget(null)}
        onConfirm={approve}
      />
    </Stack>
  )
}

export default CallTriage
